import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertyApi, Property } from '../services/api';
import { getPropertyUrl } from '../utils/formatters';
import { PriceTag } from '../design-system/components';
import { maskPropertyStatus, formatUnits } from '../domain/property/formatters';
import './NearbyPropertiesModal.css';

interface NearbyPropertiesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PropertyWithDistance extends Property {
  distance: number;
}

const NearbyPropertiesModal: React.FC<NearbyPropertiesModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nearbyProperties, setNearbyProperties] = useState<PropertyWithDistance[]>([]);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationName, setLocationName] = useState<string>('');

  // 모달 오픈 중 배경 스크롤 락 (모바일/iOS Safari 에서 배경이 함께 스크롤되는 문제 방지)
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // 두 좌표 사이의 거리 계산 (Haversine formula)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // 지구 반지름 (km)
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // 거리 포맷팅
  const formatDistance = (km: number): string => {
    if (km < 1) {
      return `${Math.round(km * 1000)}m`;
    }
    return `${km.toFixed(1)}km`;
  };

  // 정책 (Phase 7): 가격은 design-system PriceTag 경유로 "가격 문의" 통일.
  // formatPrice, formatPriceRange 함수 제거 (Concept A 정책)

  // 위치 이름 가져오기 (역지오코딩)
  const getLocationName = async (lat: number, lng: number) => {
    try {
      if (window.kakao && window.kakao.maps && window.kakao.maps.services) {
        const geocoder = new window.kakao.maps.services.Geocoder();
        geocoder.coord2Address(lng, lat, (result: any, status: any) => {
          if (status === window.kakao.maps.services.Status.OK && result[0]) {
            const address = result[0].address;
            setLocationName(`${address.region_2depth_name} ${address.region_3depth_name}`);
          }
        });
      }
    } catch (error) {
      console.error('Failed to get location name:', error);
    }
  };

  // 현재 위치 가져오기 및 주변 분양현장 검색
  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('위치 서비스를 지원하지 않는 브라우저입니다.');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        getLocationName(latitude, longitude);

        try {
          const properties = await propertyApi.getMapData();

          // 거리 계산 및 정렬
          const propertiesWithDistance: PropertyWithDistance[] = properties
            .filter((p: Property) => p.latitude && p.longitude)
            .map((property: Property) => ({
              ...property,
              distance: calculateDistance(
                latitude, longitude,
                property.latitude!, property.longitude!
              )
            }))
            .sort((a: PropertyWithDistance, b: PropertyWithDistance) => a.distance - b.distance)
            .slice(0, 10); // 상위 10개만

          setNearbyProperties(propertiesWithDistance);
        } catch (err) {
          setError('분양현장 정보를 불러오는데 실패했습니다.');
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        switch(error.code) {
          case error.PERMISSION_DENIED:
            setError('위치 권한이 거부되었습니다. 설정에서 위치 권한을 허용해주세요.');
            break;
          case error.POSITION_UNAVAILABLE:
            setError('위치 정보를 사용할 수 없습니다.');
            break;
          case error.TIMEOUT:
            setError('위치 정보 요청 시간이 초과되었습니다.');
            break;
          default:
            setError('위치를 가져오는데 실패했습니다.');
        }
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5분
      }
    );
  }, [isOpen]);

  const handlePropertyClick = (property: PropertyWithDistance) => {
    onClose();
    navigate(getPropertyUrl(property));
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="nearby-modal-overlay" onClick={onClose}></div>
      <div className="nearby-modal">
        <div className="nearby-modal-header">
          <div className="nearby-modal-title">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="location-icon">
              <circle cx="12" cy="12" r="10"></circle>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
            <div>
              <h3>주변 분양현장</h3>
              {locationName && <p className="location-name">{locationName} 주변</p>}
            </div>
          </div>
          <button className="nearby-modal-close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>

        <div className="nearby-modal-content">
          {loading && (
            <div className="nearby-loading">
              <div className="loading-spinner"></div>
              <p>현재 위치를 확인하고 있습니다...</p>
            </div>
          )}

          {error && (
            <div className="nearby-error">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p>{error}</p>
              <button onClick={onClose} className="error-close-btn">닫기</button>
            </div>
          )}

          {!loading && !error && nearbyProperties.length === 0 && (
            <div className="nearby-empty">
              <p>주변에 분양현장이 없습니다.</p>
            </div>
          )}

          {!loading && !error && nearbyProperties.length > 0 && (
            <div className="nearby-list">
              {nearbyProperties.map((property, index) => (
                <div
                  key={property.id}
                  className="nearby-item"
                  onClick={() => handlePropertyClick(property)}
                >
                  <div className="nearby-item-rank">{index + 1}</div>
                  <div className="nearby-item-content">
                    <div className="nearby-item-header">
                      <h4>{property.title}</h4>
                      <span className="nearby-item-distance">{formatDistance(property.distance)}</span>
                    </div>
                    <p className="nearby-item-address">{property.address}</p>
                    <div className="nearby-item-info">
                      <span className="nearby-item-price">
                        <PriceTag size="sm" minPrice={property.min_price} maxPrice={property.max_price} />
                      </span>
                      {property.total_units && (
                        <span className="nearby-item-units">{formatUnits(property, { short: true })}</span>
                      )}
                    </div>
                    {/* 정책: maskPropertyStatus 경유 — "분양예정" 만 chip 노출, 그 외 status 는 표시 안 함 */}
                    {(() => {
                      const label = maskPropertyStatus(
                        property.status,
                        (property as any).is_pre_registration === true,
                        (property as any).is_reopen === true,
                      );
                      return label ? (
                        <div className={`nearby-item-status status-${label}`}>{label}</div>
                      ) : null;
                    })()}
                  </div>
                  <div className="nearby-item-arrow">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {userLocation && nearbyProperties.length > 0 && (
          <div className="nearby-modal-footer">
            <button
              className="view-map-btn"
              onClick={() => {
                onClose();
                navigate('/');
              }}
            >
              지도에서 보기
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NearbyPropertiesModal;
